import React, { forwardRef, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Popup } from '@atlaskit/popup/popup';
import type { PopupComponentProps } from '@atlaskit/popup/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';

import { CUSTOM_OPTION_VALUE } from '../../../constants/filters';
import { FilterButton } from '../filter-button';
import { type SelectFilterOption } from '../select-filter';

import DateOption from './date-option';
import DateRangePicker from './date-range-picker';
import { i18n } from './messages';

// a higher layer is needed to make sure the popup is on top of the dropdown menu rendered on top of a modal
const layerAbovePopupInModal = layers.flag();

const containerStyles = xcss({
	boxSizing: 'border-box',
	width: '360px',
	overflow: 'visible',
	backgroundColor: 'elevation.surface.overlay',
	':focus': {
		outline: 'none',
	},
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.overlay',
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<Box
		{...props}
		paddingBlock="space.100"
		ref={ref}
		xcss={containerStyles}
		data-testid={undefined}
	/>
));

const formatDate = (dateString: string, locale: string) => {
	// to keep date string in local time, not UTC:
	// https://stackoverflow.com/questions/39223481/incorrect-date-shown-in-new-date-in-javascript
	const modifiedDateString = dateString.concat('T00:00');
	return new Date(modifiedDateString).toLocaleDateString(locale);
};

const createDatePickerLabel = (
	locale: string,
	afterString: string,
	beforeString: string,
	from?: string,
	to?: string,
) => {
	if (from && !to) {
		const localeFromDate = formatDate(from, locale);
		return `${afterString} ${localeFromDate}`;
	}

	if (!from && to) {
		const localeToDate = formatDate(to, locale);
		return `${beforeString} ${localeToDate}`;
	}

	if (from && to) {
		const localeFromDate = formatDate(from, locale);
		const localeToDate = formatDate(to, locale);
		return `${localeFromDate} - ${localeToDate}`;
	}
};

const getButtonLabel = (
	isSelected: boolean,
	isCustomDateSelected: boolean,
	selectedLabel: string,
	label: string,
	afterString: string,
	beforeString: string,
	locale: string,
	from?: string,
	to?: string,
) => {
	if (!isSelected) {
		return label;
	}

	if (isCustomDateSelected && (from || to)) {
		const valueLabel = createDatePickerLabel(locale, afterString, beforeString, from, to);
		return `${label}: ${valueLabel}`;
	}

	return `${label}: ${selectedLabel}`;
};

type DateFilterWithCustomRangePickerProps = {
	customDateRange: Record<'from' | 'to', string>;
	label: string;
	onDropdownOpen?: () => void;
	onSelectChange: (option: SelectFilterOption, customValue: Record<'from' | 'to', string>) => void;
	options: SelectFilterOption[];
	selectedOptions: SelectFilterOption[];
	testId?: string;
};

const DateFilterWithCustomRangePicker = ({
	options,
	onDropdownOpen,
	onSelectChange,
	selectedOptions,
	customDateRange,
	label,
	testId,
}: DateFilterWithCustomRangePickerProps): React.JSX.Element => {
	const [open, setOpen] = useState(false);
	const [customDateOpen, setCustomDateOpen] = useState(false);
	const { formatMessage, locale } = useIntl();
	const [focusedOption, setFocusedOption] = useState(0);
	const [mouseHovered, setMouseHovered] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	const isSelected = selectedOptions.length > 0;
	const firstSelected = selectedOptions[0];

	const isCustomDateSelected = firstSelected?.value === CUSTOM_OPTION_VALUE;

	const labelToDisplay = getButtonLabel(
		isSelected,
		isCustomDateSelected,
		firstSelected?.label || '',
		label,
		formatMessage(i18n.dateFilterAfterLabel),
		formatMessage(i18n.dateFilterBeforeLabel),
		locale,
		customDateRange.from,
		customDateRange.to,
	);

	const handleOptionClick = (option: SelectFilterOption) => {
		if (option.value === CUSTOM_OPTION_VALUE) {
			setCustomDateOpen(true);
		} else {
			setCustomDateOpen(false);
			onSelectChange(option, customDateRange);
			setOpen(false);
		}
	};

	const handleCustomDateRangeChange = (from: string, to: string) => {
		const option = options.find((o) => o.value === CUSTOM_OPTION_VALUE);
		if (!option) {
			return;
		}
		onSelectChange(option, { from, to });
		setOpen(false);
	};

	const handleClickDropdownBtn = () => {
		if (!open && onDropdownOpen) {
			onDropdownOpen();
		}
		setOpen(!open);
		setFocusedOption(0);
		buttonRef.current?.focus();
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setFocusedOption((prev) => (prev + 1) % options.length);
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setFocusedOption((prev) => (prev - 1 + options.length) % options.length);
		}
		if (event.key === 'Enter' && open) {
			event.preventDefault();
			handleOptionClick(options[focusedOption]);
		}
	};

	return (
		<Popup
			placement="bottom-start"
			onClose={() => setOpen(false)}
			isOpen={open}
			zIndex={layerAbovePopupInModal}
			autoFocus={false}
			popupComponent={PopupComponent}
			content={() => (
				<Box onMouseOver={() => setMouseHovered(true)} onMouseOut={() => setMouseHovered(false)}>
					{options.map((option, index) => (
						<DateOption
							hovered={!mouseHovered && focusedOption === index}
							key={option.value}
							option={option}
							onClick={() => handleOptionClick(option)}
						/>
					))}
					{(customDateOpen || isCustomDateSelected) && (
						<DateRangePicker
							onCustomDateRangeChange={handleCustomDateRangeChange}
							customDateRange={customDateRange}
						/>
					)}
				</Box>
			)}
			trigger={(triggerProps) => (
				<FilterButton
					label={labelToDisplay}
					filterOrControlId={'lastUpdated'}
					otherProps={triggerProps}
					isSelected={isSelected}
					onClick={handleClickDropdownBtn}
					testId={testId ? `${testId}-button` : undefined}
					onKeyDown={handleKeyDown}
					wide
				/>
			)}
		/>
	);
};

export default DateFilterWithCustomRangePicker;
